import { Button, Icon, Progress, Typography } from '@smartgift/frontend-toolkit';
import React, { useCallback, useEffect, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Trans, useTranslation } from 'react-i18next';

import { ACCEPTED_SHEETS } from '@hero/constants';

import * as S from './style';
import { UploadNotifications } from './UploadNotifications';

function UploadInput({
  maxFiles = 1,
  handleFile,
  fileInformation = ACCEPTED_SHEETS,
  disabled = false,
  maxMb = 1,
  apiError = null,
  status = 'idle',
  successButton,
  label = <Trans i18nKey="dragAndDropFileHere" />,
  instructions = [],
  progressIntervalTime = 100
}) {
  const { t } = useTranslation();
  const acceptableFileTypes = fileInformation.fileTypes
    .join(',')
    .toUpperCase()
    .replaceAll('.', '')
    .split(',');

  const [error, setError] = useState(null);
  const [progressRate, setProgressRate] = useState(0);

  const onDrop = useCallback((acceptedFiles, fileRejections) => {
    if (acceptedFiles.length > 0) {
      handleFile(maxFiles > 1 ? acceptedFiles : acceptedFiles[0]);
      setError(null);
    }
    fileRejections.forEach(file => {
      file.errors.forEach(err => {
        if (err.code === 'file-too-large') {
          setError(t('fileSizeTooLargeWarning', { size: maxMb }));
        }

        if (err.code === 'file-invalid-type') {
          setError(
            `${t('fileFormatNotSupportedWarning')} ${t('fileShouldBe')} ${acceptableFileTypes.join(
              ','
            )} ${t('and')} ${t('maxMBWarning', { maxMb: maxMb })}.`
          );
        }
      });
    });
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles,
    accept: fileInformation.accept,
    maxSize: maxMb ? maxMb * 1024 * 1024 : 1000000
  });

  useEffect(() => {
    let intervalId;
    if (status === 'loading') {
      intervalId = setInterval(() => {
        if (progressRate === 100) clearInterval(intervalId);
        setProgressRate(rate => (rate === 100 ? 100 : rate + 1));
      }, progressIntervalTime);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [status]);

  const RenderElement = {
    idle: (
      <S.UploadBody>
        <S.UploadBodyInput {...getInputProps()} multiple={maxFiles > 1} />
        <Icon name="upload" size="xxl" />
        <S.UploadBodyInformation>
          <Typography variant="body2">{label}</Typography>
        </S.UploadBodyInformation>
      </S.UploadBody>
    ),
    loading: <Progress text={t('uploadingFile')} showText showRate value={progressRate} />,
    success: (
      <S.UploadSuccess>
        <S.UploadSuccessIconWrapper>
          <Icon name="success" size="xl" />
        </S.UploadSuccessIconWrapper>
        <S.UploadSuccessText>
          {successButton?.text || t('fileUploadedSuccessfully')}
        </S.UploadSuccessText>
        {successButton && (
          <Button variant="text" onClick={successButton.onClick}>
            {t('replaceFile')}
          </Button>
        )}
      </S.UploadSuccess>
    )
  };

  RenderElement.error = RenderElement.idle;

  return (
    <S.UploadInput>
      <S.UploadWrapper>
        <S.UploadArea hasError={!!error || !!apiError} {...getRootProps()}>
          {disabled ? (
            <S.UploadInstructions>
              <S.UploadInstructionsSpan>
                {t('maxNumberOfFilesInLibraryWarning')}
              </S.UploadInstructionsSpan>
              <br />
              <S.UploadInstructionsSpan>{t('removeItemsLibraryWarning')}</S.UploadInstructionsSpan>
            </S.UploadInstructions>
          ) : (
            RenderElement[status]
          )}
        </S.UploadArea>

        <UploadNotifications
          status={status}
          acceptableFileTypes={acceptableFileTypes}
          maxMb={maxMb}
          instructions={instructions}
          apiError={apiError}
          error={error}
        />
      </S.UploadWrapper>
    </S.UploadInput>
  );
}

export { UploadInput };
